import { create } from 'zustand';
import { persist } from 'zustand/middleware';
type TaskBoardView = 'board' | 'list';
type TaskDrawerTab = 'info' | 'actions' | 'activity';
export type TaskStatusVisibilityKey =
  | 'pending'
  | 'progressing'
  | 'done'
  | 'failed'
  | 'other';

export type TaskStatusVisibility = Record<TaskStatusVisibilityKey, boolean>;

export const TASK_STATUS_VISIBILITY_LABELS: Record<
  TaskStatusVisibilityKey,
  { label: string; description: string }
> = {
  pending: {
    label: 'Pending',
    description: 'Queued work that has not started yet.',
  },
  progressing: {
    label: 'Progressing',
    description: 'Tasks currently running or actively being advanced.',
  },
  done: {
    label: 'Done',
    description: 'Completed work ready for review.',
  },
  failed: {
    label: 'Failed',
    description: 'Failed work waiting for retry or recovery.',
  },
  other: {
    label: 'Other status',
    description: 'Cancelled or uncommon task states.',
  },
};

export const DEFAULT_TASK_STATUS_VISIBILITY: TaskStatusVisibility = {
  pending: true,
  progressing: true,
  done: true,
  failed: true,
  other: true,
};

type ProjectTaskBoardUiState = {
  selectedTaskId: string | null;
  panelCollapsed: boolean;
  activeView: TaskBoardView;
  drawerTab: TaskDrawerTab;
  visibleStatuses: TaskStatusVisibility;
};

type ProjectTasksBoardStore = {
  byProjectId: Record<string, ProjectTaskBoardUiState>;
  ensureProjectState: (projectId: string) => void;
  setSelectedTaskId: (projectId: string, taskId: string | null) => void;
  setPanelCollapsed: (projectId: string, collapsed: boolean) => void;
  setActiveView: (projectId: string, view: TaskBoardView) => void;
  setDrawerTab: (projectId: string, tab: TaskDrawerTab) => void;
  setStatusVisible: (
    projectId: string,
    status: TaskStatusVisibilityKey,
    visible: boolean,
  ) => void;
  resetStatusVisibility: (projectId: string) => void;
};

const DEFAULT_STATE: ProjectTaskBoardUiState = {
  selectedTaskId: null,
  panelCollapsed: false,
  activeView: 'board',
  drawerTab: 'info',
  visibleStatuses: DEFAULT_TASK_STATUS_VISIBILITY,
};

function withProjectState(
  byProjectId: Record<string, ProjectTaskBoardUiState>,
  projectId: string,
): ProjectTaskBoardUiState {
  const current = byProjectId[projectId] || DEFAULT_STATE;
  return {
    ...DEFAULT_STATE,
    ...current,
    visibleStatuses: {
      ...DEFAULT_TASK_STATUS_VISIBILITY,
      ...(current.visibleStatuses || {}),
    },
  };
}

export const useProjectTasksBoardStore = create<ProjectTasksBoardStore>()(
  persist(
    (set) => ({
      byProjectId: {},
      ensureProjectState: (projectId) =>
        set((state) => {
          if (state.byProjectId[projectId]) return state;
          return {
            byProjectId: {
              ...state.byProjectId,
              [projectId]: { ...DEFAULT_STATE },
            },
          };
        }),
      setSelectedTaskId: (projectId, taskId) =>
        set((state) => ({
          byProjectId: {
            ...state.byProjectId,
            [projectId]: {
              ...withProjectState(state.byProjectId, projectId),
              selectedTaskId: taskId,
            },
          },
        })),
      setPanelCollapsed: (projectId, collapsed) =>
        set((state) => ({
          byProjectId: {
            ...state.byProjectId,
            [projectId]: {
              ...withProjectState(state.byProjectId, projectId),
              panelCollapsed: collapsed,
            },
          },
        })),
      setActiveView: (projectId, view) =>
        set((state) => ({
          byProjectId: {
            ...state.byProjectId,
            [projectId]: {
              ...withProjectState(state.byProjectId, projectId),
              activeView: view,
            },
          },
        })),
      setDrawerTab: (projectId, tab) =>
        set((state) => ({
          byProjectId: {
            ...state.byProjectId,
            [projectId]: {
              ...withProjectState(state.byProjectId, projectId),
              drawerTab: tab,
            },
          },
        })),
      setStatusVisible: (projectId, status, visible) =>
        set((state) => {
          const current = withProjectState(state.byProjectId, projectId);
          return {
            byProjectId: {
              ...state.byProjectId,
              [projectId]: {
                ...current,
                visibleStatuses: {
                  ...current.visibleStatuses,
                  [status]: visible,
                },
              },
            },
          };
        }),
      resetStatusVisibility: (projectId) =>
        set((state) => ({
          byProjectId: {
            ...state.byProjectId,
            [projectId]: {
              ...withProjectState(state.byProjectId, projectId),
              visibleStatuses: { ...DEFAULT_TASK_STATUS_VISIBILITY },
            },
          },
        })),
    }),
    {
      name: 'project-task-board-ui',
      version: 1,
    },
  ),
);
